/**
 * JSON Schema Transformations
 *
 * Modular, testable transformation functions for processing JSON schemas
 * used in RPC forms. Each transformation is independent and returns metadata
 * about what changed.
 *
 * @example
 * ```ts
 * import { processSchemaWithPipeline } from './schema-transformations';
 *
 * const result = await processSchemaWithPipeline(rawSchema, {
 *   debug: true
 * });
 * // result.schema is fully processed
 * // result.metadata shows what each step did
 * ```
 */

export * from "./types";
export { dereferenceSchema } from "./dereferenceSchema";
export { mergeAllOf } from "./mergeAllOf";
export { normalizeScalarAnyOf } from "./normalizeScalarAnyOf";
export { transformObjectAnyOf } from "./transformObjectAnyOf";

import { dereferenceSchema } from "./dereferenceSchema";
import { mergeAllOf } from "./mergeAllOf";
import { normalizeScalarAnyOf } from "./normalizeScalarAnyOf";
import { transformObjectAnyOf } from "./transformObjectAnyOf";
import type {
  JsonSchema,
  TransformOptions,
  TransformMetadata,
} from "./types";

/**
 * Result of the full transformation pipeline
 */
export interface PipelineResult {
  /** The fully transformed schema */
  schema: JsonSchema;

  /** Metadata from each transformation step */
  metadata: TransformMetadata[];

  /** Total processing time in milliseconds */
  processingTime: number;
}

/**
 * Run the complete transformation pipeline on a schema
 *
 * Pipeline order:
 * 1. dereferenceSchema - Resolve $ref pointers
 * 2. mergeAllOf - Flatten allOf (conditionally)
 * 3. normalizeScalarAnyOf - Flatten scalar anyOf, transform additionalProperties
 * 4. transformObjectAnyOf - Convert object anyOf to oneOf with discriminators
 *
 * @param rawSchema - The raw JSON schema
 * @param options - Transform options (debug, etc.)
 * @returns Promise resolving to the transformed schema and metadata
 *
 * @example
 * ```ts
 * const result = await processSchemaWithPipeline(mySchema, { debug: true });
 * console.log('Processed schema:', result.schema);
 * console.log('Steps taken:', result.metadata);
 * ```
 */
export async function processSchemaWithPipeline(
  rawSchema: JsonSchema,
  options?: TransformOptions
): Promise<PipelineResult> {
  const { debug = false } = options || {};
  const startTime = Date.now();
  const metadata: TransformMetadata[] = [];

  if (debug) {
    console.log(
      "%c🔍 Schema Processing Pipeline",
      "font-weight: bold; font-size: 14px; color: #00bcd4; background: #1a1a1a; padding: 8px 12px; border-radius: 4px;"
    );
    console.groupCollapsed("%cRaw Schema", "color: #888; font-style: italic;");
    console.log(rawSchema);
    console.groupEnd();
  }

  try {
    // Step 1: Dereference $ref pointers
    const step1 = await dereferenceSchema(rawSchema, options);
    metadata.push(step1.metadata);
    let currentSchema = step1.schema;

    // Step 2: Merge allOf (conditional)
    const step2 = mergeAllOf(currentSchema, options);
    metadata.push(step2.metadata);
    currentSchema = step2.schema;

    // Step 3: Normalize scalar anyOf + additionalProperties
    const step3 = normalizeScalarAnyOf(currentSchema, options);
    metadata.push(step3.metadata);
    currentSchema = step3.schema;

    // Step 4: Transform object anyOf to oneOf
    const step4 = transformObjectAnyOf(currentSchema, options);
    metadata.push(step4.metadata);
    currentSchema = step4.schema;

    const processingTime = Date.now() - startTime;

    if (debug) {
      console.log(
        "%c✅ Pipeline Complete",
        "font-weight: bold; color: #4caf50; font-size: 13px;"
      );
      console.log(
        `%c⏱️  Processing Time: ${processingTime}ms`,
        "color: #ff9800; font-weight: bold;"
      );

      // Format metadata for table display
      const tableData = metadata.map((meta) => ({
        "Step": meta.name,
        "Status": meta.skipped ? "⏭️  Skipped" : "✅ Complete",
        "Keys Changed": meta.transformedKeys.length,
        "Reason": meta.reason || "-",
      }));

      console.log("%cPipeline Steps:", "font-weight: bold; color: #9c27b0;");
      console.table(tableData);

      console.groupCollapsed("%cFinal Schema", "color: #888; font-style: italic;");
      console.log(currentSchema);
      console.groupEnd();
    }

    return {
      schema: currentSchema,
      metadata,
      processingTime,
    };
  } catch (err) {
    if (debug) {
      console.error(
        "%c❌ Pipeline Failed",
        "font-weight: bold; color: #f44336; font-size: 13px;"
      );
      console.error(err);
    }
    throw err;
  }
}
